import { useEffect, useState } from 'react';
import type { GhlContext } from './types';
import { getGhlContext } from './ghl/sso';
import { Picker } from './pages/Picker';
import { Documents } from './pages/Documents';
import { EditorPage } from './pages/Editor';

type Tab = 'create' | 'documents';

type View =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'tab'; tab: Tab }
  | { kind: 'editor'; documentId: string; from: Tab };

export function App() {
  const [ghl, setGhl] = useState<GhlContext | null>(null);
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [contactId, setContactId] = useState<string | null>(null);

  useEffect(() => {
    // 1. SSO handshake with the parent GHL frame.
    getGhlContext()
      .then((ctx) => {
        console.info('[pdc] GHL context ready', {
          locationId: ctx.locationId,
          email: ctx.email,
          userId: ctx.userId,
          hasEncrypted: Boolean(ctx.encrypted),
        });
        setGhl(ctx);
        // 2. Deep-link support: ?contactId=…, ?documentId=… and ?tab=documents
        const params = new URLSearchParams(window.location.search);
        const c = params.get('contactId');
        const d = params.get('documentId');
        const tab: Tab = params.get('tab') === 'documents' ? 'documents' : 'create';
        if (c) setContactId(c);
        if (d) {
          setView({ kind: 'editor', documentId: d, from: tab });
        } else {
          setView({ kind: 'tab', tab });
        }
      })
      .catch((e: Error) => {
        console.error('[pdc] init failed:', e.message);
        setView({ kind: 'error', message: e.message });
      });
  }, []);

  if (view.kind === 'loading') return <Centered>Loading…</Centered>;
  if (view.kind === 'error')
    return (
      <Centered>
        <strong>Could not establish GHL session</strong>
        <p style={{ color: '#6b7280', marginTop: 8 }}>{view.message}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 16,
            padding: '8px 18px',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
        <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 16 }}>
          For local dev, append <code>?ghlStub=1</code> to bypass.
        </p>
      </Centered>
    );

  if (!ghl) return <Centered>Loading…</Centered>;

  if (view.kind === 'editor') {
    return (
      <EditorPage
        ghl={ghl}
        documentId={view.documentId}
        onBack={() => setView({ kind: 'tab', tab: view.from })}
      />
    );
  }

  const tab = view.tab;
  return (
    <div>
      <header style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px 0' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>PandaDoc · MFR</h1>
        <nav style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb' }}>
          {([['create', 'New document'], ['documents', 'Signed documents']] as const).map(([t, label]) => (
            <button key={t} onClick={() => setView({ kind: 'tab', tab: t })} style={tabBtn(tab === t)}>
              {label}
            </button>
          ))}
        </nav>
      </header>

      {tab === 'create' ? (
        <Picker
          ghl={ghl}
          initialContactId={contactId}
          onDocumentCreated={(documentId) => setView({ kind: 'editor', documentId, from: 'create' })}
        />
      ) : (
        <Documents
          ghl={ghl}
          initialContactId={contactId}
          onOpenDocument={(documentId) => setView({ kind: 'editor', documentId, from: 'documents' })}
        />
      )}
    </div>
  );
}

function tabBtn(active: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    color: active ? '#2563eb' : '#6b7280',
    fontWeight: active ? 600 : 500,
    fontSize: 14,
    cursor: 'pointer',
  };
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24, textAlign: 'center' }}>
      <div>{children}</div>
    </div>
  );
}
